import moment from 'moment';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  CorrectiveActionsPriorityEnum,
  CorrectiveActionsStatusEnum,
  IncidentsSeverityEnum,
} from '@EHSNavigator/backend/dist/api';
import { IncidentsStatusEnum } from '@EHSNavigator/backend/dist/api';

const severityColorMap = {
  [IncidentsSeverityEnum.Critical]: 'b6244f', // e.g., posh-pink
  [IncidentsSeverityEnum.Major]: 'ed7a3a', // e.g., fair-orange
  [IncidentsSeverityEnum.Moderate]: 'f6cd13', // e.g., primary-yellow
  [IncidentsSeverityEnum.Low]: '168553', // e.g., papery/default
};
const statusColorMap = {
  [IncidentsStatusEnum.Active]: 'b6244f', // e.g., posh-pink
  [IncidentsStatusEnum.Closed]: '168553', // e.g., fair-orange
  [IncidentsStatusEnum.InProgress]: '3957df', // e.g., primary-yellow
};

const correctiveActionsPriorityColorMap = {
  [CorrectiveActionsPriorityEnum.Urgent]: 'b6244f', // e.g., posh-pink
  [CorrectiveActionsPriorityEnum.High]: 'ed7a3a', // e.g., fair-orange
  [CorrectiveActionsPriorityEnum.Medium]: 'f6cd13', // e.g., primary-yellow
  [CorrectiveActionsPriorityEnum.Low]: '168553', // e.g., papery/default
};
const correctiveActionsStatusColorMap = {
  [CorrectiveActionsStatusEnum.Active]: 'b6244F', // e.g., posh-pink
  [CorrectiveActionsStatusEnum.Closed]: '168553', // e.g., fair-orange
  [CorrectiveActionsStatusEnum.InReview]: '3957df', // e.g., primary-yellow
};

export const handleExportToExcel = async (
  exportData: any,
  headers: any,
  columnWidths: any,
  type?: string,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Incidents');

  // Fetch image buffer with error handling
  let logoImage: ArrayBuffer | null = null;
  try {
    const response = await fetch('/assets/images/logo.png');
    if (response.ok) {
      logoImage = await response.arrayBuffer();
    }
  } catch (error) {
    console.warn('Failed to fetch logo image:', error);
  }

  // Add image to workbook
  const imageId = workbook.addImage({
    buffer: logoImage as ArrayBuffer,
    extension: 'png',
  });
  worksheet.addImage(imageId, {
    tl: { col: 3.44, row: 0.24 },
    ext: { width: 80, height: 110 },
  });
  worksheet.mergeCells('A1:G1');
  worksheet.getRow(1).height = 90;
  const imageCell = worksheet.getCell('A1');
  imageCell.alignment = {
    vertical: 'middle',
    horizontal: 'center',
  };
  imageCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  imageCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  worksheet.mergeCells('A2:G2');
  const titleCell = worksheet.getCell('A2');
  worksheet.getRow(2).height = 35;
  titleCell.value = 'EHS Navigator';
  titleCell.font = {
    name: 'Roboto',
    size: 28,
    bold: true,
    color: { argb: '3957DF' },
  };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  titleCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  worksheet.mergeCells('A3:G3');
  const subHeadingCell = worksheet.getCell('A3');
  worksheet.getRow(3).height = 25;
  subHeadingCell.value =
    type === 'incident-review'
      ? 'Review all incidents from this list and assign corrective actions.'
      : 'All incidents that have been reported by you are listed here.';
  subHeadingCell.font = {
    name: 'Roboto',
    size: 15,
    color: { argb: 'FF333333' },
  };
  subHeadingCell.alignment = { vertical: 'middle', horizontal: 'center' };
  subHeadingCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  subHeadingCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  // // Date of export
  worksheet.mergeCells('A4:G4');
  const dateCell = worksheet.getCell('A4');
  worksheet.getRow(4).height = 25;
  dateCell.value = `Date of Export: ${moment().format('MMM D, YYYY')}`;
  dateCell.font = { name: 'Roboto', size: 13, bold: true };
  dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
  dateCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  dateCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  // Define columns
  worksheet.columns = columnWidths;

  // // Add header row at row 6
  const headerRow = worksheet.getRow(5);
  headerRow.values = headers;
  headerRow.height = 25;

  exportData.forEach((rowData: any, idx: number) => {
    const row = worksheet.getRow(6 + idx);
    row.values = [
      rowData['Sr.'],
      rowData['Ref#'],
      rowData['Incident/Hazard'],
      rowData['Date of Report'],
      rowData.Type,
      rowData.Severity,
      rowData.Status,
    ];
    row.commit();
  });

  // // Style header row
  worksheet.getRow(5).eachCell((cell) => {
    cell.font = {
      name: 'Roboto',
      size: 12,
      bold: true,
      color: { argb: '121212' },
    };
    cell.border = {
      top: { style: 'thin', color: { argb: '999999' } },
      left: { style: 'thin', color: { argb: '999999' } },
      bottom: { style: 'thin', color: { argb: '999999' } },
      right: { style: 'thin', color: { argb: '999999' } },
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'EFEFEF' },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });

  // // Add fill color f6f8f9 to odd rows (excluding header)
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return; // skip header
    if (rowNumber % 2 === 1) {
      // odd row
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F6F8F9' },
        };
      });
    }
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return;
    row.height = 25;
    row.eachCell((cell, colNumber) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.font = { size: 11, name: 'Roboto', color: { argb: '121212' } };

      if (colNumber === 1) {
        cell.font = {
          size: 11,
          name: 'Roboto',
          color: { argb: '999999' },
          bold: true,
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFF' },
        };
      }
    });
  });
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return;

    row.height = 25;
    row.eachCell((cell, colNumber) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.font = { size: 11, name: 'Roboto', color: { argb: '121212' } };

      // Sr Column styling
      if (colNumber === 1) {
        cell.font = {
          size: 11,
          name: 'Roboto',
          color: { argb: '999999' },
          bold: true,
        };
      }

      // Severity Column styling
      if (colNumber === 6) {
        const severity = cell.value;
        const fillColor =
          severityColorMap[severity as keyof typeof severityColorMap] ||
          '3957DF';
        cell.font = { ...cell.font, color: { argb: fillColor }, bold: true };
      }

      // Status Column styling
      if (colNumber === 7) {
        const status = cell.value;
        const fillColor =
          statusColorMap[status as keyof typeof statusColorMap] || '3957df';
        cell.font = { ...cell.font, color: { argb: fillColor }, bold: true };
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `incidents_export_${new Date().toISOString().split('T')[0]}.xlsx`;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
};

export const handleAssignedCorrectiveActionsExportToExcel = async (
  exportData: any,
  headers: any,
  columnWidths: any,
  type?: string,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Incidents');

  // Fetch image buffer with error handling
  let logoImage: ArrayBuffer | null = null;
  try {
    const response = await fetch('/assets/images/logo.png');
    if (response.ok) {
      logoImage = await response.arrayBuffer();
    }
  } catch (error) {
    console.warn('Failed to fetch logo image:', error);
  }

  // Add image to workbook
  const imageId = workbook.addImage({
    buffer: logoImage as ArrayBuffer,
    extension: 'png',
  });
  worksheet.addImage(imageId, {
    tl: { col: 3.44, row: 0.24 },
    ext: { width: 80, height: 110 },
  });
  worksheet.mergeCells('A1:G1');
  worksheet.getRow(1).height = 90;
  const imageCell = worksheet.getCell('A1');
  imageCell.alignment = {
    vertical: 'middle',
    horizontal: 'center',
  };
  imageCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  imageCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  worksheet.mergeCells('A2:G2');
  const titleCell = worksheet.getCell('A2');
  worksheet.getRow(2).height = 35;
  titleCell.value = 'EHS Navigator';
  titleCell.font = {
    name: 'Roboto',
    size: 28,
    bold: true,
    color: { argb: '3957DF' },
  };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  titleCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  worksheet.mergeCells('A3:G3');
  const subHeadingCell = worksheet.getCell('A3');
  worksheet.getRow(3).height = 25;
  subHeadingCell.value =
    type === 'assigned-corrective-actions'
      ? ' Review all corrective actions that have been assigned to employees from this list.'
      : 'Review all corrective actions that you have assigned to employees from this list.';
  subHeadingCell.font = {
    name: 'Roboto',
    size: 15,
    color: { argb: 'FF333333' },
  };
  subHeadingCell.alignment = { vertical: 'middle', horizontal: 'center' };
  subHeadingCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  subHeadingCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  // // Date of export
  worksheet.mergeCells('A4:G4');
  const dateCell = worksheet.getCell('A4');
  worksheet.getRow(4).height = 25;
  dateCell.value = `Date of Export: ${moment().format('MMM D, YYYY')}`;
  dateCell.font = { name: 'Roboto', size: 13, bold: true };
  dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
  dateCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  dateCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  // Define columns
  worksheet.columns = columnWidths;

  // // Add header row at row 6
  const headerRow = worksheet.getRow(5);
  headerRow.values = headers;
  headerRow.height = 25;

  exportData.forEach((rowData: any, idx: number) => {
    const row = worksheet.getRow(6 + idx);
    row.values = [
      rowData['Sr.'],
      rowData['Ref#'],
      rowData['Corrective Action'],
      rowData['Due Date'],
      rowData.Department,
      rowData.Priority,
      rowData.Status,
    ];
    row.commit();
  });

  // // Style header row
  worksheet.getRow(5).eachCell((cell) => {
    cell.font = {
      name: 'Roboto',
      size: 12,
      bold: true,
      color: { argb: '121212' },
    };
    cell.border = {
      top: { style: 'thin', color: { argb: '999999' } },
      left: { style: 'thin', color: { argb: '999999' } },
      bottom: { style: 'thin', color: { argb: '999999' } },
      right: { style: 'thin', color: { argb: '999999' } },
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'EFEFEF' },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });

  // // Add fill color f6f8f9 to odd rows (excluding header)
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return; // skip header
    if (rowNumber % 2 === 1) {
      // odd row
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F6F8F9' },
        };
      });
    }
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return;
    row.height = 25;
    row.eachCell((cell, colNumber) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.font = { size: 11, name: 'Roboto', color: { argb: '121212' } };

      if (colNumber === 1) {
        cell.font = {
          size: 11,
          name: 'Roboto',
          color: { argb: '999999' },
          bold: true,
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFF' },
        };
      }
    });
  });
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return;

    row.height = 25;
    row.eachCell((cell, colNumber) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.font = { size: 11, name: 'Roboto', color: { argb: '121212' } };

      // Sr Column styling
      if (colNumber === 1) {
        cell.font = {
          size: 11,
          name: 'Roboto',
          color: { argb: '999999' },
          bold: true,
        };
      }

      // Severity Column styling
      if (colNumber === 6) {
        const severity = cell.value;
        const fillColor =
          correctiveActionsPriorityColorMap[
          severity as keyof typeof correctiveActionsPriorityColorMap
          ] || '3957DF';
        cell.font = { ...cell.font, color: { argb: fillColor }, bold: true };
      }

      // Status Column styling
      if (colNumber === 7) {
        const status = cell.value;
        const fillColor =
          correctiveActionsStatusColorMap[
          status as keyof typeof correctiveActionsStatusColorMap
          ] || '3957df';
        cell.font = { ...cell.font, color: { argb: fillColor }, bold: true };
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `assigned_corrective_actions_export_${new Date().toISOString().split('T')[0]}.xlsx`;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
};

export const handleCorrectiveActionsAssignedToMeExportToExcel = async (
  exportData: any,
  headers: any,
  columnWidths: any,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Incidents');

  // Fetch image buffer with error handling
  let logoImage: ArrayBuffer | null = null;
  try {
    const response = await fetch('/assets/images/logo.png');
    if (response.ok) {
      logoImage = await response.arrayBuffer();
    }
  } catch (error) {
    console.warn('Failed to fetch logo image:', error);
  }

  // Add image to workbook
  const imageId = workbook.addImage({
    buffer: logoImage as ArrayBuffer,
    extension: 'png',
  });
  worksheet.addImage(imageId, {
    tl: { col: 3.44, row: 0.24 },
    ext: { width: 80, height: 110 },
  });
  worksheet.mergeCells('A1:G1');
  worksheet.getRow(1).height = 90;
  const imageCell = worksheet.getCell('A1');
  imageCell.alignment = {
    vertical: 'middle',
    horizontal: 'center',
  };
  imageCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  imageCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  worksheet.mergeCells('A2:G2');
  const titleCell = worksheet.getCell('A2');
  worksheet.getRow(2).height = 35;
  titleCell.value = 'EHS Navigator';
  titleCell.font = {
    name: 'Roboto',
    size: 28,
    bold: true,
    color: { argb: '3957DF' },
  };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  titleCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  worksheet.mergeCells('A3:G3');
  const subHeadingCell = worksheet.getCell('A3');
  worksheet.getRow(3).height = 25;
  subHeadingCell.value =
    'Review all corrective actions assigned to you by your manager from this list and take action on them.';
  subHeadingCell.font = {
    name: 'Roboto',
    size: 15,
    color: { argb: 'FF333333' },
  };
  subHeadingCell.alignment = { vertical: 'middle', horizontal: 'center' };
  subHeadingCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  subHeadingCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  // // Date of export
  worksheet.mergeCells('A4:G4');
  const dateCell = worksheet.getCell('A4');
  worksheet.getRow(4).height = 25;
  dateCell.value = `Date of Export: ${moment().format('MMM D, YYYY')}`;
  dateCell.font = { name: 'Roboto', size: 13, bold: true };
  dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
  dateCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  dateCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  // Define columns
  worksheet.columns = columnWidths;

  // // Add header row at row 6
  const headerRow = worksheet.getRow(5);
  headerRow.values = headers;
  headerRow.height = 25;

  exportData.forEach((rowData: any, idx: number) => {
    const row = worksheet.getRow(6 + idx);
    row.values = [
      rowData['Sr.'],
      rowData['Ref#'],
      rowData['Corrective Action'],
      rowData['Due Date'],
      rowData['Assigned By'],
      rowData.Priority,
      rowData.Status,
    ];
    row.commit();
  });

  // // Style header row
  worksheet.getRow(5).eachCell((cell) => {
    cell.font = {
      name: 'Roboto',
      size: 12,
      bold: true,
      color: { argb: '121212' },
    };
    cell.border = {
      top: { style: 'thin', color: { argb: '999999' } },
      left: { style: 'thin', color: { argb: '999999' } },
      bottom: { style: 'thin', color: { argb: '999999' } },
      right: { style: 'thin', color: { argb: '999999' } },
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'EFEFEF' },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });

  // // Add fill color f6f8f9 to odd rows (excluding header)
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return; // skip header
    if (rowNumber % 2 === 1) {
      // odd row
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F6F8F9' },
        };
      });
    }
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return;
    row.height = 25;
    row.eachCell((cell, colNumber) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.font = { size: 11, name: 'Roboto', color: { argb: '121212' } };

      if (colNumber === 1) {
        cell.font = {
          size: 11,
          name: 'Roboto',
          color: { argb: '999999' },
          bold: true,
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFF' },
        };
      }
    });
  });
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return;

    row.height = 25;
    row.eachCell((cell, colNumber) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.font = { size: 11, name: 'Roboto', color: { argb: '121212' } };

      // Sr Column styling
      if (colNumber === 1) {
        cell.font = {
          size: 11,
          name: 'Roboto',
          color: { argb: '999999' },
          bold: true,
        };
      }

      // Severity Column styling
      if (colNumber === 6) {
        const severity = cell.value;
        const fillColor =
          correctiveActionsPriorityColorMap[
          severity as keyof typeof correctiveActionsPriorityColorMap
          ] || '3957DF';
        cell.font = { ...cell.font, color: { argb: fillColor }, bold: true };
      }

      // Status Column styling
      if (colNumber === 7) {
        const status = cell.value;
        const fillColor =
          correctiveActionsStatusColorMap[
          status as keyof typeof correctiveActionsStatusColorMap
          ] || '3957df';
        cell.font = { ...cell.font, color: { argb: fillColor }, bold: true };
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `assigned_corrective_actions_export_${new Date().toISOString().split('T')[0]}.xlsx`;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
};

export const handleExportToCSV = (
  exportData: any,
  headers: any,
  label?: string,
) => {
  let csvContent = headers.join(',') + '\n';

  exportData.forEach((row: any) => {
    const rowValues = headers.map((header: string) => {
      const value = row[header] || '';
      return `"${value.toString().replace(/"/g, '""')}"`;
    });
    csvContent += rowValues.join(',') + '\n';
  });

  // Create and download CSV file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const fileName = label
    ? `${label}_export_${moment().format('YYYY-MM-DD')}.csv`
    : `incidents_export_${moment().format('YYYY-MM-DD')}.csv`;
  saveAs(blob, fileName);
};

export const handleInjuriesExportToExcel = async (
  exportData: any,
  headers: any,
  columnWidths: any,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Incidents');

  // Fetch image buffer with error handling
  let logoImage: ArrayBuffer | null = null;
  try {
    const response = await fetch('/assets/images/logo.png');
    if (response.ok) {
      logoImage = await response.arrayBuffer();
    }
  } catch (error) {
    console.warn('Failed to fetch logo image:', error);
  }

  // Add image to workbook
  const imageId = workbook.addImage({
    buffer: logoImage as ArrayBuffer,
    extension: 'png',
  });
  worksheet.addImage(imageId, {
    tl: { col: 3.99, row: 0.15 },
    ext: { width: 80, height: 110 },
  });
  worksheet.mergeCells('A1:G1');
  worksheet.getRow(1).height = 90;
  const imageCell = worksheet.getCell('A1');
  imageCell.alignment = {
    vertical: 'middle',
    horizontal: 'center',
  };
  imageCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  imageCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  worksheet.mergeCells('A2:G2');
  const titleCell = worksheet.getCell('A2');
  worksheet.getRow(2).height = 35;
  titleCell.value = 'EHS Navigator';
  titleCell.font = {
    name: 'Roboto',
    size: 28,
    bold: true,
    color: { argb: '3957DF' },
  };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  titleCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  worksheet.mergeCells('A3:G3');
  const subHeadingCell = worksheet.getCell('A3');
  worksheet.getRow(3).height = 25;
  subHeadingCell.value =
    'Review all Injuries/Illnesses that were reported in your organization.';
  subHeadingCell.font = {
    name: 'Roboto',
    size: 15,
    color: { argb: 'FF333333' },
  };
  subHeadingCell.alignment = { vertical: 'middle', horizontal: 'center' };
  subHeadingCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  subHeadingCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  // // Date of export
  worksheet.mergeCells('A4:G4');
  const dateCell = worksheet.getCell('A4');
  worksheet.getRow(4).height = 25;
  dateCell.value = `Date of Export: ${moment().format('MMM D, YYYY')}`;
  dateCell.font = { name: 'Roboto', size: 13, bold: true };
  dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
  dateCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  dateCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  // Define columns
  worksheet.columns = columnWidths;

  // // Add header row at row 6
  const headerRow = worksheet.getRow(5);
  headerRow.values = headers;
  headerRow.height = 25;

  exportData.forEach((rowData: any, idx: number) => {
    const row = worksheet.getRow(6 + idx);
    row.values = [
      rowData['Sr.'],
      rowData['Ref#'],
      rowData['Title'],
      rowData['Employee Affected'],
      rowData.Severity,
      rowData['Investigation Status'],
      rowData['Corrective Action Status'],
    ];
    row.commit();
  });

  // // Style header row
  worksheet.getRow(5).eachCell((cell) => {
    cell.font = {
      name: 'Roboto',
      size: 12,
      bold: true,
      color: { argb: '121212' },
    };
    cell.border = {
      top: { style: 'thin', color: { argb: '999999' } },
      left: { style: 'thin', color: { argb: '999999' } },
      bottom: { style: 'thin', color: { argb: '999999' } },
      right: { style: 'thin', color: { argb: '999999' } },
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'EFEFEF' },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });

  // // Add fill color f6f8f9 to odd rows (excluding header)
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return; // skip header
    if (rowNumber % 2 === 1) {
      // odd row
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F6F8F9' },
        };
      });
    }
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return;
    row.height = 25;
    row.eachCell((cell, colNumber) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.font = { size: 11, name: 'Roboto', color: { argb: '121212' } };

      if (colNumber === 1) {
        cell.font = {
          size: 11,
          name: 'Roboto',
          color: { argb: '999999' },
          bold: true,
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFF' },
        };
      }
    });
  });
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return;

    row.height = 25;
    row.eachCell((cell, colNumber) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.font = { size: 11, name: 'Roboto', color: { argb: '121212' } };

      // Sr Column styling
      if (colNumber === 1) {
        cell.font = {
          size: 11,
          name: 'Roboto',
          color: { argb: '999999' },
          bold: true,
        };
      }

      // Severity Column styling
      if (colNumber === 5) {
        const severity = cell.value;
        const fillColor =
          severityColorMap[severity as keyof typeof severityColorMap] ||
          '3957DF';
        cell.font = { ...cell.font, color: { argb: fillColor }, bold: true };
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `injuries_export_${new Date().toISOString().split('T')[0]}.xlsx`;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
};





export const handleOshaReportingLogsExportToExcel = async (
  exportData: any,
  headers: any,
  columnWidths: any,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Incidents');

  // Fetch image buffer with error handling
  let logoImage: ArrayBuffer | null = null;
  try {
    const response = await fetch('/assets/images/logo.png');
    if (response.ok) {
      logoImage = await response.arrayBuffer();
    }
  } catch (error) {
    console.warn('Failed to fetch logo image:', error);
  }

  // Add image to workbook
  const imageId = workbook.addImage({
    buffer: logoImage as ArrayBuffer,
    extension: 'png',
  });
  worksheet.addImage(imageId, {
    tl: { col: 3.44, row: 0.24 },
    ext: { width: 80, height: 110 },
  });
  worksheet.mergeCells('A1:G1');
  worksheet.getRow(1).height = 90;
  const imageCell = worksheet.getCell('A1');
  imageCell.alignment = {
    vertical: 'middle',
    horizontal: 'center',
  };
  imageCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  imageCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  worksheet.mergeCells('A2:G2');
  const titleCell = worksheet.getCell('A2');
  worksheet.getRow(2).height = 35;
  titleCell.value = 'EHS Navigator';
  titleCell.font = {
    name: 'Roboto',
    size: 28,
    bold: true,
    color: { argb: '3957DF' },
  };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  titleCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  worksheet.mergeCells('A3:G3');
  const subHeadingCell = worksheet.getCell('A3');
  worksheet.getRow(3).height = 25;
  subHeadingCell.value =
    'Automatically generated from your injuries/illnesses data. Review for accuracy before certification.';
  subHeadingCell.font = {
    name: 'Roboto',
    size: 15,
    color: { argb: 'FF333333' },
  };
  subHeadingCell.alignment = { vertical: 'middle', horizontal: 'center' };
  subHeadingCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  subHeadingCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  // // Date of export
  worksheet.mergeCells('A4:G4');
  const dateCell = worksheet.getCell('A4');
  worksheet.getRow(4).height = 25;
  dateCell.value = `Date of Export: ${moment().format('MMM D, YYYY')}`;
  dateCell.font = { name: 'Roboto', size: 13, bold: true };
  dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
  dateCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F6F8F9' },
  };
  dateCell.border = {
    top: { style: 'thin', color: { argb: 'e1e1e7' } },
    left: { style: 'thin', color: { argb: 'e1e1e7' } },
    bottom: { style: 'thin', color: { argb: 'e1e1e7' } },
    right: { style: 'thin', color: { argb: 'e1e1e7' } },
  };

  // Define columns
  worksheet.columns = columnWidths;

  // // Add header row at row 6
  const headerRow = worksheet.getRow(5);
  headerRow.values = headers;
  headerRow.height = 25;

  exportData.forEach((rowData: any, idx: number) => {
    const row = worksheet.getRow(6 + idx);
    row.values = [
      rowData['Sr.'],
      rowData['Ref#'],
      rowData.Status,
      rowData['Employee Effected'],
      rowData['Date'],
      rowData['Type Of Loss'],
      rowData['Failed Reason'],
    ];
    row.commit();
  });

  // // Style header row
  worksheet.getRow(5).eachCell((cell) => {
    cell.font = {
      name: 'Roboto',
      size: 12,
      bold: true,
      color: { argb: '121212' },
    };
    cell.border = {
      top: { style: 'thin', color: { argb: '999999' } },
      left: { style: 'thin', color: { argb: '999999' } },
      bottom: { style: 'thin', color: { argb: '999999' } },
      right: { style: 'thin', color: { argb: '999999' } },
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'EFEFEF' },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });

  // // Add fill color f6f8f9 to odd rows (excluding header)
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return; // skip header
    if (rowNumber % 2 === 1) {
      // odd row
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F6F8F9' },
        };
      });
    }
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return;
    row.height = 25;
    row.eachCell((cell, colNumber) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.font = { size: 11, name: 'Roboto', color: { argb: '121212' } };

      if (colNumber === 1) {
        cell.font = {
          size: 11,
          name: 'Roboto',
          color: { argb: '999999' },
          bold: true,
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFF' },
        };
      }
    });
  });
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber < 6) return;

    row.height = 25;
    row.eachCell((cell, colNumber) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.font = { size: 11, name: 'Roboto', color: { argb: '121212' } };

      // Sr Column styling
      if (colNumber === 1) {
        cell.font = {
          size: 11,
          name: 'Roboto',
          color: { argb: '999999' },
          bold: true,
        };
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `incidents_export_${new Date().toISOString().split('T')[0]}.xlsx`;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
};
